import React, { createContext, useContext, useCallback, useState, useEffect, useRef, ReactNode } from 'react';
import { Plugin, PluginRegistryType, PluginRoute, RegisteredRoute, RouteRegistrationOptions } from './types';
import { usePluginScriptManager } from './useHookScript';
import { PlaygroundProvider, getPlaygroundConfig } from './PlaygroundProvider';
import { PlaygroundConfig } from './usePlaygroundClient';
import { Thunder } from '@ptkl/sdk';

const PluginRegistryContext = createContext<PluginRegistryType | undefined>(undefined);

export { PluginRegistryContext };

export interface PluginRegistryProviderProps {
  children: ReactNode;
  repository?: string;
  initialPlugins?: Plugin[];
  onPluginRegister?: (plugin: Plugin) => void;
  onPluginUnregister?: (pluginName: string) => void;
  enableDynamicLoading?: boolean;
  playgroundConfig?: Partial<PlaygroundConfig>;
}

export const PluginRegistryProvider = ({ 
  children, 
  repository = 'plugins',
  initialPlugins = [],
  onPluginRegister,
  onPluginUnregister,
  enableDynamicLoading = true,
  playgroundConfig
}: PluginRegistryProviderProps) => {
  // Mark initial plugins with source if not already set
  const markedInitialPlugins = initialPlugins.map(plugin => ({
    ...plugin,
    meta: {
      ...plugin.meta,
      source: plugin.meta.source || 'initial'
    }
  }));
  
  const [plugins, setPlugins] = useState<Plugin[]>(markedInitialPlugins);
  const [routes, setRoutes] = useState<RegisteredRoute[]>([]);
  const [loadedScripts, setLoadedScripts] = useState<HTMLScriptElement[]>([]);
  const [isLoading, setIsLoading] = useState(enableDynamicLoading);
  const [expectedPluginCount, setExpectedPluginCount] = useState(0);
  const [minimumLoadTime, setMinimumLoadTime] = useState(enableDynamicLoading);
  const hasLoadedRef = useRef(false);
  const registerPluginRef = useRef<(plugin: Plugin) => void>(() => {});

  // Initialize the script manager with access to the plugins
  usePluginScriptManager(() => plugins);

  // Route management methods
  const generateRouteId = useCallback((pluginName: string, path: string, namespace?: string) => {
    const parts = [pluginName, path];
    if (namespace) parts.unshift(namespace);
    return parts.join(':');
  }, []);

  const registerRoute = useCallback((route: PluginRoute, pluginName: string, options: RouteRegistrationOptions = {}) => {
    const { namespace, replace = false, priority = 0 } = options;
    const routeId = generateRouteId(pluginName, route.path, namespace);
    
    const registeredRoute: RegisteredRoute = {
      ...route,
      pluginName,
      namespace,
      id: routeId,
      registeredAt: new Date(),
      order: route.order ?? priority
    };

    setRoutes(prev => {
      const existingIndex = prev.findIndex(r => r.id === routeId);
      
      if (existingIndex !== -1) {
        if (replace) {
          const newRoutes = [...prev];
          newRoutes[existingIndex] = registeredRoute;
          return newRoutes.sort((a, b) => (b.order ?? 0) - (a.order ?? 0));
        } else {
          console.warn(`Route with ID ${routeId} already exists. Use replace: true to override.`);
          return prev;
        }
      } else {
        return [...prev, registeredRoute].sort((a, b) => (b.order ?? 0) - (a.order ?? 0));
      }
    });
  }, [generateRouteId]);

  const unregisterRoute = useCallback((routeId: string) => {
    setRoutes(prev => prev.filter(route => route.id !== routeId));
  }, []);

  const unregisterPluginRoutes = useCallback((pluginName: string) => {
    setRoutes(prev => prev.filter(route => route.pluginName !== pluginName));
  }, []);

  const getRoutes = useCallback((namespace?: string) => {
    if (namespace) {
      return routes.filter(route => route.namespace === namespace);
    }
    return routes;
  }, [routes]);

  const getRoutesByPlugin = useCallback((pluginName: string) => {
    return routes.filter(route => route.pluginName === pluginName);
  }, [routes]);

  const registerPlugin = useCallback((plugin: Plugin) => {
    setPlugins(prev => {
      const existingIndex = prev.findIndex(p => p.meta.name === plugin.meta.name);
      if (existingIndex !== -1) {
        const existingPlugin = prev[existingIndex];
        const newPlugins = [...prev];
        
        // Priority system: playground > installed > initial
        const getSourcePriority = (source?: string) => {
          switch (source) {
            case 'playground': return 3;
            case 'installed': return 2;
            case 'initial': return 1;
            default: return 0; // Unknown source gets lowest priority
          }
        };
        
        const existingPriority = getSourcePriority(existingPlugin.meta.source);
        const newPriority = getSourcePriority(plugin.meta.source);
        
        // Only replace if new plugin has higher or equal priority
        if (newPriority >= existingPriority) {
          console.log(`🔌 Plugin "${plugin.meta.name}" replacing existing plugin (${plugin.meta.source || 'unknown'} >= ${existingPlugin.meta.source || 'unknown'})`);
          newPlugins[existingIndex] = plugin;
        } else {
          console.log(`🔌 Plugin "${plugin.meta.name}" registration blocked - existing ${existingPlugin.meta.source} plugin has higher priority than ${plugin.meta.source}`);
          return prev; // Don't replace - existing plugin has higher priority
        }
        
        return newPlugins;
      } else {
        console.log(`🔌 Plugin "${plugin.meta.name}" registered as new plugin (source: ${plugin.meta.source || 'unknown'})`);
        return [...prev, plugin];
      }
    });
    
    // Register plugin routes if provided
    if (plugin.routes) {
      if (Array.isArray(plugin.routes)) {
        // Direct route array
        plugin.routes.forEach(route => {
          console.log('Registering route:', route.path, 'for plugin:', plugin.meta.name);
          registerRoute(route, plugin.meta.name);
        });
      } else if (typeof plugin.routes === 'function') {
        // Function that registers routes
        plugin.routes((route: PluginRoute, options?: RouteRegistrationOptions) => {
          console.log('Dynamically registering route:', route.path, 'for plugin:', plugin.meta.name);
          registerRoute(route, plugin.meta.name, options);
        });
      }
    }
    
    if (onPluginRegister) {
      onPluginRegister(plugin);
    }
  }, [onPluginRegister, registerRoute]);

  // Check if all plugins are registered
  useEffect(() => {
    if (isLoading && expectedPluginCount > 0 && plugins.length >= expectedPluginCount) {
      setIsLoading(false);
    }
  }, [plugins.length, expectedPluginCount, isLoading]);

  // Minimum loading time to ensure plugins have time to register
  useEffect(() => {
    if (enableDynamicLoading && minimumLoadTime) {
      const timer = setTimeout(() => {
        setMinimumLoadTime(false);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [enableDynamicLoading, minimumLoadTime]);

  registerPluginRef.current = registerPlugin;

  // Process initial plugins for route registration
  useEffect(() => {
    if (initialPlugins.length > 0) {
      console.log('Processing initial plugins for route registration:', initialPlugins.length);
      initialPlugins.forEach(plugin => {
        if (plugin.routes) {
          if (Array.isArray(plugin.routes)) {
            // Direct route array
            plugin.routes.forEach(route => {
              console.log('Registering initial route:', route.path, 'for plugin:', plugin.meta.name);
              registerRoute(route, plugin.meta.name);
            });
          } else if (typeof plugin.routes === 'function') {
            // Function that registers routes
            plugin.routes((route: PluginRoute, options?: RouteRegistrationOptions) => {
              console.log('Dynamically registering initial route:', route.path, 'for plugin:', plugin.meta.name);
              registerRoute(route, plugin.meta.name, options);
            });
          }
        }
      });
    }
  }, [initialPlugins, registerRoute]);

  // Ensure React is available globally for plugins
  const ensureGlobalReact = useCallback(() => {
    if (typeof window !== 'undefined' && !window.React) {
      window.React = React;
      globalThis.React = React;
    }
  }, []);

  // Create import map for React compatibility
  const ensureImportMap = useCallback(() => {
    if (document.querySelector('script[type="importmap"]')) return;
    
    ensureGlobalReact();
    
    // Generate all React exports dynamically
    const reactExports = Object.keys(React).filter(key => key !== 'default').map(key => `export const ${key}=React.${key};`).join('');
    
    const importMap = {
      imports: {
        "react": `data:text/javascript;charset=utf-8,const React=globalThis.React;export default React;${reactExports}`,
        "react/jsx-runtime": "data:text/javascript;charset=utf-8,export const jsx=globalThis.React.createElement;export const jsxs=globalThis.React.createElement;export const Fragment=globalThis.React.Fragment;"
      }
    };

    const script = document.createElement('script');
    script.type = 'importmap';
    script.textContent = JSON.stringify(importMap);
    document.head.insertBefore(script, document.head.firstChild);
  }, [ensureGlobalReact]);

  useEffect(() => {
    ensureImportMap();
  }, [ensureImportMap]);

  // Inject plugin script
  const injectPluginScript = useCallback((pluginData: any) => {
    return new Promise<void>((resolve) => {
      if (!pluginData.code || typeof pluginData.code !== 'string') {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'module';
      script.id = `plugin-${pluginData.name || 'unknown'}-${Date.now()}`;
      
      let wrappedCode = pluginData.code;
      
      // Auto-registration for export patterns
      const exportMatch = pluginData.code.match(/export\s+default\s+(\w+);?\s*$/m);
      const rollupExportMatch = pluginData.code.match(/export\s*\{\s*(\w+)\s+as\s+default\s*\};?\s*$/m);
      
      const pluginVariableName = exportMatch?.[1] || rollupExportMatch?.[1];
      
      if (pluginVariableName) {
        const registrationCode = `
if (typeof window !== "undefined" && window.registerPlugin && typeof ${pluginVariableName} !== "undefined") {
  console.log('🔌 Auto-registering plugin:', ${pluginVariableName});
  window.registerPlugin(${pluginVariableName});
} else {
  console.warn('⚠️ Failed to auto-register plugin. window.registerPlugin:', typeof window?.registerPlugin, 'plugin:', typeof ${pluginVariableName});
}`;
        
        if (exportMatch) {
          wrappedCode = pluginData.code.replace(/export\s+default\s+(\w+);?\s*$/m, `export default $1;${registrationCode}`);
        } else {
          wrappedCode = pluginData.code.replace(/export\s*\{\s*(\w+)\s+as\s+default\s*\};?\s*$/m, `export { $1 as default };${registrationCode}`);
        }
      } else {
        console.warn('⚠️ No export pattern found in plugin code for auto-registration');
      }
      
      script.textContent = wrappedCode;
      script.onload = () => resolve();
      script.onerror = () => resolve();
      
      document.head.appendChild(script);
      setLoadedScripts(prev => [...prev, script]);
    });
  }, []);

  // Set up global registerPlugin function for playground
  useEffect(() => {
    const finalPlaygroundConfig = { ...getPlaygroundConfig(), ...playgroundConfig };
    const shouldEnablePlayground = finalPlaygroundConfig.enabled;
    
    // Always expose registerPlugin globally when playground is enabled with source preservation
    if (shouldEnablePlayground && typeof window !== 'undefined') {
      const originalRegisterPlugin = registerPluginRef.current;
      window.registerPlugin = (plugin: Plugin) => {
        // Preserve existing source or mark as installed if not from playground
        if (!plugin.meta.source && !plugin.meta.isPlayground) {
          plugin.meta.source = 'installed';
          console.log(`🔌 Playground: Marking non-playground plugin "${plugin.meta.name}" as installed`);
        } else if (plugin.meta.isPlayground && !plugin.meta.source) {
          plugin.meta.source = 'playground';
          console.log(`🔌 Playground: Marking playground plugin "${plugin.meta.name}" as playground`);
        }
        return originalRegisterPlugin(plugin);
      };
      console.log('🔌 Playground: window.registerPlugin set up for playground with source preservation');
      
      return () => {
        if (typeof window !== 'undefined' && window.registerPlugin) {
          delete window.registerPlugin;
          console.log('🔌 Playground: window.registerPlugin cleaned up');
        }
      };
    }
  }, [playgroundConfig]);

  // Load plugins dynamically
  useEffect(() => {
    if (!enableDynamicLoading || hasLoadedRef.current) {
      if (!enableDynamicLoading) setIsLoading(false);
      return;
    }

    const loadDynamicPlugins = async () => {
      hasLoadedRef.current = true;
      
      try {
        // Get playground configuration
        const finalPlaygroundConfig = { ...getPlaygroundConfig(), ...playgroundConfig };
        const shouldEnablePlayground = finalPlaygroundConfig.enabled;
        
        // Check if Thunder SDK is available for loading plugins
        if (typeof Thunder === 'undefined') {
          console.warn('⚠️ Thunder SDK not available, skipping Thunder plugin loading');
          setIsLoading(false);
          return;
        }
        
        const thunder = new Thunder();
        const response = await thunder.find(`${repository}`, { active: true });
        const dynamicPlugins = response?.data || [];
        
        console.log(`🔌 Thunder: Found ${dynamicPlugins.length} active plugins in repository: ${repository}`);
        setExpectedPluginCount(dynamicPlugins.length);
        
        if (dynamicPlugins.length === 0) {
          console.log('🔌 Thunder: No active plugins found, skipping plugin loading');
          setIsLoading(false);
          return;
        }

        // Set timeout fallback
        setTimeout(() => {
          setIsLoading(false);
        }, 10000);

        // Expose registerPlugin globally for Thunder plugins with source tagging
        if (typeof window !== 'undefined') {
          const originalRegisterPlugin = registerPluginRef.current;
          window.registerPlugin = (plugin: Plugin) => {
            // Mark Thunder plugins if they don't already have a source
            if (!plugin.meta.source) {
              plugin.meta.source = 'installed';
              console.log(`🔌 Thunder: Marking plugin "${plugin.meta.name}" as installed`);
            }
            return originalRegisterPlugin(plugin);
          };
          console.log('🔌 Thunder: registerPlugin exposed globally for Thunder plugins with source tagging');
        }
        
        // Load each plugin
        dynamicPlugins.forEach(async (pluginData: any) => {
          try {
            console.log('🔌 Thunder: Loading plugin:', pluginData.name || 'unknown');
            if (pluginData.code) {
              await injectPluginScript(pluginData);
              console.log('🔌 Thunder: Successfully injected code for plugin:', pluginData.name);
            } else if (pluginData.url) {
              const script = document.createElement('script');
              script.type = 'module';
              script.src = pluginData.url;
              script.id = `plugin-url-${pluginData.name || 'unknown'}-${Date.now()}`;
              
              document.head.appendChild(script);
              setLoadedScripts(prev => [...prev, script]);
              console.log('🔌 Thunder: Successfully loaded script for plugin:', pluginData.name);
            }
          } catch (error) {
            console.error('🔌 Thunder: Failed to load plugin:', pluginData.name, error);
          }
        });

      } catch (error) {
        console.error('🔌 Thunder: Error loading Thunder plugins:', error);
        setIsLoading(false);
      }
    };

    loadDynamicPlugins();

    // Cleanup
    return () => {
      loadedScripts.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
      
      // Only clean up if we set it in this effect (Thunder mode)
      const finalPlaygroundConfig = { ...getPlaygroundConfig(), ...playgroundConfig };

      const shouldEnablePlayground = finalPlaygroundConfig.enabled;
      if (!shouldEnablePlayground && typeof window !== 'undefined' && window.registerPlugin === registerPluginRef.current) {
        delete window.registerPlugin;
      }
    };
  }, [repository, enableDynamicLoading, ensureImportMap, injectPluginScript, playgroundConfig]);

  const getPluginsForSlot = (slot: string) => {
    return plugins.filter(p => p.meta.slot === slot || (p.slots && p.slots[slot]));
  };

  const unregisterPlugin = (pluginName: string) => {
    setPlugins(prev => prev.filter(p => p.meta.name !== pluginName));
    // Also unregister all routes for this plugin
    unregisterPluginRoutes(pluginName);
    if (onPluginUnregister) {
      onPluginUnregister(pluginName);
    }
  };

  const getRegistryInfo = () => ({
    name: repository,
    pluginCount: plugins.length,
    pluginNames: plugins.map(p => p.meta.name)
  });

  // Handle playground plugin updates - process them like Thunder plugins
  const handlePlaygroundPluginUpdate = useCallback((plugin: Plugin) => {
    // For now, directly register the plugin since it's already processed
    registerPlugin(plugin);
  }, [registerPlugin]);

  const handlePlaygroundCodeUpdate = useCallback(async (pluginName: string, code: string) => {
    try {
      // Process the playground code the same way as Thunder plugins
      const pluginData = {
        name: pluginName,
        code: code,
        isPlayground: true
      };
      
      await injectPluginScript(pluginData);
    } catch (error) {
      console.error(`Playground plugin error for ${pluginName}:`, error);
    }
  }, [injectPluginScript]);

  const handlePlaygroundPluginError = useCallback((pluginName: string, error: string) => {
    console.error(`Playground plugin error for ${pluginName}:`, error);
  }, []);

  // Get playground configuration
  const finalPlaygroundConfig = { ...getPlaygroundConfig(), ...playgroundConfig };
  const shouldEnablePlayground = finalPlaygroundConfig.enabled;

  const content = (
    <PluginRegistryContext.Provider value={{ 
      plugins, 
      registerPlugin, 
      getPluginsForSlot,
      unregisterPlugin,
      getRegistryInfo,
      isLoading,
      routes,
      registerRoute,
      unregisterRoute,
      unregisterPluginRoutes,
      getRoutes,
      getRoutesByPlugin
    }}>
      {(!enableDynamicLoading || (!isLoading && !minimumLoadTime)) ? (
        children
      ) : (
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          zIndex: 9999,
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }} />
          <div style={{ color: '#666', fontSize: '14px' }}>
            Loading plugins... ({plugins.length}/{expectedPluginCount || '?'})
          </div>
          <style>
            {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
          </style>
        </div>
      )}
    </PluginRegistryContext.Provider>
  );

  // Wrap with playground provider if enabled
  if (shouldEnablePlayground) {
    return (
      <PlaygroundProvider
        config={finalPlaygroundConfig}
        onPluginUpdate={handlePlaygroundPluginUpdate}
        onCodeUpdate={handlePlaygroundCodeUpdate}
        onPluginError={handlePlaygroundPluginError}
        showDebugPanel={finalPlaygroundConfig.showDebugPanel}
        showMinimalIndicator={finalPlaygroundConfig.showMinimalIndicator}
      >
        {content}
      </PlaygroundProvider>
    );
  }

  return content;
};

export const usePluginRegistry = () => {
  const ctx = useContext(PluginRegistryContext);
  if (!ctx) throw new Error('usePluginRegistry must be used within PluginRegistryProvider');
  return ctx;
};